<template>
  <div class="gh-heatmap">
    <h3 style="margin: 0;">{{ username }} (<a :href="`https://github.com/${username}`">GitHub</a>)</h3>
    <img :src="`https://ghchart.rshah.org/${encodeURIComponent(username)}`" :alt="`${username} contributions`"/>

    <div class="stats-container">
      <div class="stat-box" title="Nombre total de repositories que que je possède">
        <span v-if="loading.owned_total"><span class="spinner"></span></span>
        <span v-else>{{ animated.owned_total }}</span> Repositories
      </div>
      <div class="stat-box" title="Nombre total de repositories auxquels j'ai contribué mais que je ne possède pas">
        <span v-if="loading.participations"><span class="spinner"></span></span>
        <span v-else>{{ animated.contributed_excl_own_total }}</span> Participations
      </div>
      <div class="stat-box" title="Nombre total de commits réalisés depuis la création du compte">
        <span v-if="loading.commits"><span class="spinner"></span></span>
        <span v-else>{{ animated.total_commits_all_time }}</span> Commits
      </div>
    </div>
  </div>
</template>

<script>
import {GetRequest} from '@/services/request';

export default {
  name: 'GitHubInfo',

  props: {
    username: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      loading: {
        owned_total: true,
        participations: true,
        commits: true
      },
      stats: {
        owned_total: 0,
        contributed_excl_own_total: 0,
        total_commits_all_time: 0
      },
      animated: {
        owned_total: 0,
        contributed_excl_own_total: 0,
        total_commits_all_time: 0
      },
      rafIds: {}
    }
  },

  methods: {
    animateValue(key, start, end, duration = 800) {
      if (this.rafIds[key]) cancelAnimationFrame(this.rafIds[key]);
      const startTime = performance.now();
      const step = (now) => {
        const progress = Math.min((now - startTime) / duration, 1);
        this.animated[key] = Math.floor(start + (end - start) * progress);
        if (progress < 1) this.rafIds[key] = requestAnimationFrame(step);
      }
      this.rafIds[key] = requestAnimationFrame(step);
    },

    updateAnimatedStat(key) {
      this.animateValue(key, this.animated[key], this.stats[key]);
    },

    async fetchOwnedTotal() {
      try {
        const data = await GetRequest(`github/owned-total/${this.username}`);
        this.stats.owned_total = Number(data.owned_total ?? 0);
      } finally {
        this.loading.owned_total = false;
        this.updateAnimatedStat('owned_total');
      }
    },

    async fetchParticipations() {
      try {
        const data = await GetRequest(`github/participations/${this.username}`);
        this.stats.contributed_excl_own_total = Number(data.contributed_excl_own_total ?? 0);
      } finally {
        this.loading.participations = false;
        this.updateAnimatedStat('contributed_excl_own_total');
      }
    },

    async fetchCommits() {
      try {
        const data = await GetRequest(`github/commits/${this.username}`);
        this.stats.total_commits_all_time = Number(data.total_commits_all_time ?? 0);
      } finally {
        this.loading.commits = false;
        this.updateAnimatedStat('total_commits_all_time');
      }
    }
  },

  mounted() {
    this.fetchOwnedTotal();
    this.fetchParticipations();
    this.fetchCommits();
  }
}
</script>

<style scoped>
.gh-heatmap {
  display: flex;
  flex-direction: column;
  max-width: 100%;
  box-sizing: border-box;
  background: #323336;
  border: 1px solid #30363d;
  border-radius: 8px;
  padding: 10px 12px;
  color: #c9d1d9;
}

.stats-container {
  display: flex;
  justify-content: space-around;
  margin-top: 10px;
}

.stat-box {
  background: #1e1f22;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 0.95rem;
}

.spinner {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid #ccc;
  border-top-color: #58a6ff;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
  vertical-align: middle;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
